<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  campaign: {
    type: Object,
    required: true,
  },
  isRefreshing: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['back', 'refresh']);

const { t } = useI18n();
const currentPage = ref(1);
const itemsPerPage = ref(50);

const emptyValue = computed(() => t('CAMPAIGN.WHATSAPP.REPORTS.UNAVAILABLE'));
const pageSizeOptions = [
  { value: 15, label: '15' },
  { value: 30, label: '30' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
];

const templateName = computed(() => {
  return props.campaign.template_params?.name || emptyValue.value;
});

const audienceLabel = computed(() => {
  const labels = props.campaign.report_summary?.audience_labels || [];
  return labels.length ? labels.join(', ') : emptyValue.value;
});

const recipients = computed(() => props.campaign.report_recipients || []);
const totalRecipients = computed(() => recipients.value.length);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(totalRecipients.value / itemsPerPage.value))
);
const paginatedRecipients = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  return recipients.value.slice(start, start + itemsPerPage.value);
});
const pageStart = computed(() => {
  if (!totalRecipients.value) return 0;

  return (currentPage.value - 1) * itemsPerPage.value + 1;
});
const pageEnd = computed(() =>
  Math.min(currentPage.value * itemsPerPage.value, totalRecipients.value)
);

const formatDateTime = value => {
  if (!value) return emptyValue.value;

  const timestamp = typeof value === 'number' ? value * 1000 : value;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return emptyValue.value;

  return date.toLocaleString();
};

const formatCount = value => {
  return value === null || value === undefined ? emptyValue.value : value;
};

const truncateText = (value, length = 72) => {
  if (!value) return emptyValue.value;

  return value.length > length ? `${value.slice(0, length)}...` : value;
};

const summaryItems = computed(() => [
  {
    label: t('CAMPAIGN.WHATSAPP.REPORTS.SUMMARY.NAME'),
    value: props.campaign.title,
  },
  {
    label: t('CAMPAIGN.WHATSAPP.REPORTS.SUMMARY.TEMPLATE'),
    value: templateName.value,
  },
  {
    label: t('CAMPAIGN.WHATSAPP.REPORTS.SUMMARY.INBOX'),
    value: props.campaign.inbox?.name || emptyValue.value,
  },
  {
    label: t('CAMPAIGN.WHATSAPP.REPORTS.SUMMARY.AUDIENCE'),
    value: audienceLabel.value,
  },
  {
    label: t('CAMPAIGN.WHATSAPP.REPORTS.SUMMARY.CREATED_AT'),
    value: formatDateTime(props.campaign.created_at),
  },
  {
    label: t('CAMPAIGN.WHATSAPP.REPORTS.SUMMARY.SCHEDULED_AT'),
    value: formatDateTime(props.campaign.scheduled_at),
  },
  {
    label: t('CAMPAIGN.WHATSAPP.REPORTS.SUMMARY.STATUS'),
    value: props.campaign.campaign_status || emptyValue.value,
  },
  {
    label: t('CAMPAIGN.WHATSAPP.REPORTS.SUMMARY.TOTAL_TARGETS'),
    value: formatCount(props.campaign.report_summary?.total_targets),
  },
  {
    label: t('CAMPAIGN.WHATSAPP.REPORTS.SUMMARY.SENT'),
    value: formatCount(props.campaign.report_summary?.sent),
  },
  {
    label: t('CAMPAIGN.WHATSAPP.REPORTS.SUMMARY.FAILED'),
    value: formatCount(props.campaign.report_summary?.failed),
  },
  {
    label: t('CAMPAIGN.WHATSAPP.REPORTS.SUMMARY.DELIVERED'),
    value: formatCount(props.campaign.report_summary?.delivered),
  },
  {
    label: t('CAMPAIGN.WHATSAPP.REPORTS.SUMMARY.READ'),
    value: formatCount(props.campaign.report_summary?.read),
  },
]);

const goToPage = page => {
  currentPage.value = Math.min(Math.max(page, 1), totalPages.value);
};

watch(itemsPerPage, () => {
  currentPage.value = 1;
});

watch(totalPages, value => {
  if (currentPage.value > value) currentPage.value = value;
});
</script>

<template>
  <section class="flex flex-col gap-6">
    <div class="flex items-center justify-between gap-3">
      <div>
        <h2 class="text-xl font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.REPORTS.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.REPORTS.DESCRIPTION') }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <Button
          variant="faded"
          color="slate"
          icon="i-lucide-refresh-cw"
          :label="t('CAMPAIGN.WHATSAPP.REPORTS.REFRESH')"
          :is-loading="isRefreshing"
          @click="emit('refresh')"
        />
        <Button
          variant="faded"
          color="slate"
          icon="i-lucide-arrow-left"
          :label="t('CAMPAIGN.WHATSAPP.REPORTS.BACK')"
          @click="emit('back')"
        />
      </div>
    </div>

    <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
      <div
        v-for="item in summaryItems"
        :key="item.label"
        class="p-4 rounded-lg border border-n-weak bg-n-alpha-1"
      >
        <div class="text-xs text-n-slate-11">
          {{ item.label }}
        </div>
        <div class="mt-1 text-sm font-medium break-words text-n-slate-12">
          {{ item.value || emptyValue }}
        </div>
      </div>
    </div>

    <div class="overflow-hidden rounded-lg border border-n-weak">
      <div class="px-4 py-3 border-b border-n-weak">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.REPORTS.RECIPIENTS_TITLE') }}
        </h3>
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-[1100px] table-fixed text-sm">
          <thead class="bg-n-alpha-2 text-n-slate-11">
            <tr>
              <th class="w-40 px-4 py-3 font-medium text-start">
                {{ t('CAMPAIGN.WHATSAPP.REPORTS.TABLE.NAME') }}
              </th>
              <th class="w-40 px-4 py-3 font-medium text-start">
                {{ t('CAMPAIGN.WHATSAPP.REPORTS.TABLE.PHONE') }}
              </th>
              <th class="w-32 px-4 py-3 font-medium text-start">
                {{ t('CAMPAIGN.WHATSAPP.REPORTS.TABLE.STATUS') }}
              </th>
              <th class="w-40 px-4 py-3 font-medium text-start">
                {{ t('CAMPAIGN.WHATSAPP.REPORTS.TABLE.SENT_AT') }}
              </th>
              <th class="w-40 px-4 py-3 font-medium text-start">
                {{ t('CAMPAIGN.WHATSAPP.REPORTS.TABLE.DELIVERED_AT') }}
              </th>
              <th class="w-40 px-4 py-3 font-medium text-start">
                {{ t('CAMPAIGN.WHATSAPP.REPORTS.TABLE.READ_AT') }}
              </th>
              <th class="w-64 px-4 py-3 font-medium text-start">
                {{ t('CAMPAIGN.WHATSAPP.REPORTS.TABLE.WAMID') }}
              </th>
              <th class="w-72 px-4 py-3 font-medium text-start">
                {{ t('CAMPAIGN.WHATSAPP.REPORTS.TABLE.FAILURE_REASON') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="recipient in paginatedRecipients"
              :key="`${recipient.phone_number}-${recipient.name}`"
              class="border-t border-n-weak"
            >
              <td class="px-4 py-3 text-n-slate-12">
                {{ recipient.name || emptyValue }}
              </td>
              <td class="px-4 py-3 text-n-slate-12">
                {{ recipient.phone_number || emptyValue }}
              </td>
              <td class="px-4 py-3 text-n-slate-12">
                {{ recipient.status || emptyValue }}
              </td>
              <td class="px-4 py-3 text-n-slate-12">
                {{ formatDateTime(recipient.sent_at) }}
              </td>
              <td class="px-4 py-3 text-n-slate-12">
                {{ formatDateTime(recipient.delivered_at) }}
              </td>
              <td class="px-4 py-3 text-n-slate-12">
                {{ formatDateTime(recipient.read_at) }}
              </td>
              <td class="px-4 py-3 text-n-slate-12">
                <span
                  class="block truncate"
                  :title="recipient.wamid || emptyValue"
                >
                  {{ truncateText(recipient.wamid) }}
                </span>
              </td>
              <td class="px-4 py-3 text-n-slate-12">
                <span
                  class="block truncate"
                  :title="recipient.failure_reason || emptyValue"
                >
                  {{ truncateText(recipient.failure_reason) }}
                </span>
              </td>
            </tr>
            <tr v-if="totalRecipients === 0">
              <td colspan="8" class="px-4 py-8 text-center text-n-slate-11">
                {{ t('CAMPAIGN.WHATSAPP.REPORTS.EMPTY_RECIPIENTS') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        v-if="totalRecipients"
        class="flex min-h-[3.375rem] flex-col gap-4 border-t border-n-weak px-4 py-3 lg:flex-row lg:items-center lg:justify-between"
      >
        <div class="flex flex-wrap items-center gap-3 text-sm text-n-slate-11">
          <span>
            {{ t('CAMPAIGN.WHATSAPP.REPORTS.ROWS_PER_PAGE') }}
          </span>
          <ComboBox
            v-model="itemsPerPage"
            :options="pageSizeOptions"
            class="w-20 [&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
          />
          <span>
            {{
              t('CAMPAIGN.WHATSAPP.REPORTS.PAGE_RANGE', {
                start: pageStart,
                end: pageEnd,
                total: totalRecipients,
              })
            }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <Button
            icon="i-lucide-chevron-left"
            variant="ghost"
            color="slate"
            size="sm"
            class="!h-8 !w-8"
            :disabled="currentPage === 1"
            @click="goToPage(currentPage - 1)"
          />
          <span class="text-sm text-n-slate-11">
            {{
              t('CAMPAIGN.WHATSAPP.REPORTS.PAGE_COUNT', {
                current: currentPage,
                total: totalPages,
              })
            }}
          </span>
          <Button
            icon="i-lucide-chevron-right"
            variant="ghost"
            color="slate"
            size="sm"
            class="!h-8 !w-8"
            :disabled="currentPage === totalPages"
            @click="goToPage(currentPage + 1)"
          />
        </div>
      </div>
    </div>
  </section>
</template>
